import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Vendor Hunter Modal — Piece #2 (Free Tier).
 * Three tabs:
 *   1. Prospect Queue — every prospect sorted (pending first, then score desc)
 *   2. Add Single — manual paste of one vendor (name + trade + community tags)
 *   3. Bulk CSV — download template, upload filled CSV
 */
const VendorHunterModal = ({ open, onClose }) => {
  const [tab, setTab] = useState("queue");
  const [prospects, setProspects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [rProspects, rSummary] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/vendor-hunter/prospects`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/vendor-hunter/summary`, { credentials: "include" }),
      ]);
      if (rProspects.ok) setProspects((await rProspects.json()).rows || []);
      if (rSummary.ok) setSummary(await rSummary.json());
    } catch (e) {
      setErr("Could not load Hunter data. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <div
      data-testid="vendor-hunter-modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(2,8,20,0.78)",
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        padding: "3rem 1.5rem", zIndex: 9998, overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(160deg, #0a1929 0%, #1b2a4e 100%)",
          border: "1px solid rgba(184,145,90,0.5)",
          borderRadius: 10, maxWidth: 1000, width: "100%",
          padding: "1.8rem 2rem 1.6rem", color: "#F5EFE1",
          boxShadow: "0 40px 80px rgba(0,0,0,0.5)",
        }}
        data-testid="vendor-hunter-modal"
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem", flexWrap: "wrap", gap: "0.7rem" }}>
          <div>
            <div style={{ color: "#B8915A", fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
              Digital Platform · Vendor Hunter
            </div>
            <h2 style={{ margin: "0.3rem 0 0", fontSize: "1.4rem", fontWeight: 700 }}>
              🎯 Free-Tier Vendor Discovery + Auto-Scoring
            </h2>
            <div style={{ color: "#ADBED1", fontSize: "0.85rem", marginTop: 4 }}>
              Add DFW contractor prospects manually or in bulk. The system auto-scores each against your Tier A / Tier B doctrine.
            </div>
          </div>
          <button
            type="button"
            data-testid="vendor-hunter-close"
            onClick={onClose}
            style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A", padding: "0.45rem 0.9rem", borderRadius: 4, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
          >
            Close ✕
          </button>
        </div>

        {/* Summary strip */}
        {summary && (
          <div data-testid="vendor-hunter-summary" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.6rem", marginBottom: "1.1rem" }}>
            {[
              { label: "Pending YES", value: summary.pending, color: "#FFB347", testid: "vh-count-pending" },
              { label: "Approved", value: summary.approved, color: "#00DC78", testid: "vh-count-approved" },
              { label: "Invited",  value: summary.invited,  color: "#5AC8D8", testid: "vh-count-invited" },
              { label: "Declined", value: summary.declined, color: "#9CA3AF", testid: "vh-count-declined" },
            ].map((k) => (
              <div key={k.label} data-testid={k.testid} style={{ background: "rgba(0,0,0,0.28)", border: `1px solid ${k.color}55`, borderRadius: 5, padding: "0.55rem 0.8rem" }}>
                <div style={{ color: k.color, fontSize: "1.4rem", fontWeight: 800 }}>{k.value}</div>
                <div style={{ color: "#ADBED1", fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Bulk Dispatch bar — routes approved queue through comm_scrubber → Twilio + Resend + Stripe Connect Express */}
        {summary && summary.approved > 0 && (
          <DispatchAllBar approvedCount={summary.approved} onDispatched={load} />
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0.4rem", borderBottom: "1px solid rgba(184,145,90,0.3)", marginBottom: "1rem", flexWrap: "wrap" }}>
          {[
            { k: "queue",  label: `Prospect Queue${summary ? ` (${summary.pending})` : ""}`, testid: "vh-tab-queue" },
            { k: "manual", label: "Add Single Prospect", testid: "vh-tab-manual" },
            { k: "csv",    label: "Bulk CSV Upload",     testid: "vh-tab-csv" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              data-testid={t.testid}
              onClick={() => setTab(t.k)}
              style={{
                background: tab === t.k ? "rgba(184,145,90,0.2)" : "transparent",
                color: tab === t.k ? "#F5EFE1" : "#B8915A",
                border: "none",
                borderBottom: tab === t.k ? "2px solid #B8915A" : "2px solid transparent",
                padding: "0.55rem 1rem", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {err && (
          <div style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)", color: "#FCA5A5", padding: "0.6rem 0.9rem", borderRadius: 5, marginBottom: "0.9rem" }}>
            ⚠ {err}
          </div>
        )}

        {loading && <div style={{ color: "#ADBED1", padding: "1rem 0" }}>Loading…</div>}

        {tab === "queue" && !loading && <QueueTab prospects={prospects} onDecide={load} />}
        {tab === "manual" && <ManualTab onSaved={() => { setTab("queue"); load(); }} />}
        {tab === "csv" && <CsvTab onImported={() => { setTab("queue"); load(); }} />}
      </div>
    </div>
  );
};


const QueueTab = ({ prospects, onDecide }) => {
  const decide = async (id, decision) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendor-hunter/prospect/${id}/decide`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, reason: "" }),
      });
      if (r.ok) onDecide();
      else alert(`Could not save decision: HTTP ${r.status}`);
    } catch { alert("Network error saving decision."); }
  };

  if (prospects.length === 0) {
    return (
      <div data-testid="vh-queue-empty" style={{ color: "#ADBED1", padding: "1.5rem 0.5rem", textAlign: "center" }}>
        No prospects yet. Add one manually or upload a CSV to get started.
      </div>
    );
  }

  return (
    <div data-testid="vh-queue" style={{ display: "grid", gap: "0.55rem", maxHeight: "60vh", overflowY: "auto" }}>
      {prospects.map((p) => {
        const scoreColor = p.score >= 80 ? "#00DC78" : p.score >= 60 ? "#FFB347" : "#F87171";
        const decColor  = p.decision === "approved" ? "#00DC78"
                        : p.decision === "invited"  ? "#5AC8D8"
                        : p.decision === "declined" ? "#9CA3AF" : "#FFB347";
        return (
          <div key={p.prospect_id} data-testid={`vh-prospect-${p.prospect_id}`}
               style={{ background: "rgba(0,0,0,0.22)", padding: "0.7rem 0.85rem", borderRadius: 5, borderLeft: `3px solid ${scoreColor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: "#F3F4F6", fontSize: "0.95rem", fontWeight: 700 }}>
                  {p.business_name}
                  {p.community_tags && p.community_tags.length > 0 && (
                    <span style={{ marginLeft: "0.5rem", fontSize: "0.7rem", color: "#E8B8C4", fontWeight: 700 }}>
                      · {p.community_tags.map((t) => t.replace("_", "-")).join(" · ")}
                    </span>
                  )}
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: 2 }}>
                  {p.trade || "—"} · {p.city || "—"} {p.zip_code} · Tier <strong>{p.tier || "?"}</strong>
                  {p.employee_count && <> · {p.employee_count} emp</>}
                  {p.owner_is_operator && <> · owner-op</>}
                </div>
                {p.reasons && p.reasons.length > 0 && (
                  <div style={{ color: "#9CA3AF", fontSize: "0.72rem", marginTop: 3, fontStyle: "italic" }}>
                    {p.reasons.slice(0, 3).join(" · ")}
                  </div>
                )}
                {p.red_flags && p.red_flags.length > 0 && (
                  <div style={{ color: "#F87171", fontSize: "0.72rem", marginTop: 3, fontWeight: 700 }}>
                    ⚠ {p.red_flags.join(" · ")}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <div data-testid={`vh-score-${p.prospect_id}`} style={{ color: scoreColor, fontSize: "1.2rem", fontWeight: 800 }}>
                  {p.score}/100
                </div>
                <div style={{ color: decColor, fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                  {p.decision}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 5, marginTop: "0.55rem", flexWrap: "wrap" }}>
              {p.decision === "pending" && (
                <>
                  <button type="button" data-testid={`vh-approve-${p.prospect_id}`}
                          onClick={() => decide(p.prospect_id, "approved")}
                          style={btnStyle("#00DC78", "#062617")}>✓ Approve</button>
                  <button type="button" data-testid={`vh-invite-${p.prospect_id}`}
                          onClick={() => decide(p.prospect_id, "invited")}
                          style={btnStyle("#5AC8D8", "#062617")}>📧 Invite to Apply</button>
                  <button type="button" data-testid={`vh-decline-${p.prospect_id}`}
                          onClick={() => decide(p.prospect_id, "declined")}
                          style={btnStyle("transparent", "#F87171", "#F87171")}>✕ Decline</button>
                </>
              )}
              {p.trec_lookup_url && (
                <a href={p.trec_lookup_url} target="_blank" rel="noreferrer"
                   data-testid={`vh-trec-${p.prospect_id}`}
                   style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A",
                            padding: "0.35rem 0.75rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700,
                            textDecoration: "none", cursor: "pointer" }}>
                  🏛 TREC lookup ↗
                </a>
              )}
              {p.website && (
                <a href={p.website} target="_blank" rel="noreferrer"
                   style={{ background: "transparent", color: "#ADBED1", border: "1px solid rgba(255,255,255,0.2)",
                            padding: "0.35rem 0.75rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700,
                            textDecoration: "none", cursor: "pointer" }}>
                  🌐 Website ↗
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};


const btnStyle = (bg, fg, border) => ({
  background: bg, color: fg,
  border: border ? `1px solid ${border}` : "none",
  padding: "0.35rem 0.75rem", borderRadius: 4,
  fontSize: "0.75rem", fontWeight: 800, cursor: "pointer",
});


const DispatchAllBar = ({ approvedCount, onDispatched }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const dispatchAll = async () => {
    if (!window.confirm(
      `Dispatch institutional partnership invites to all ${approvedCount} approved trade prospects?\n\n` +
      `Each contact will receive a scrubbed email + SMS with a Stripe Connect Express onboarding link.\n\n` +
      `Live delivery is gated by SANDBOX_MODE — in preview environments this stages but does not send.`
    )) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendor-hunter/dispatch-approved`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dispatch: true, limit: 500 }),
      });
      const d = await r.json();
      if (r.ok) {
        setResult(d);
        setTimeout(onDispatched, 1200);
      } else {
        setErr(d.detail || `HTTP ${r.status}`);
      }
    } catch (e) {
      setErr("Network error dispatching.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="vh-dispatch-all-bar"
         style={{ background: "linear-gradient(90deg, rgba(90,200,216,0.18), rgba(0,220,120,0.14))",
                  border: "1px solid rgba(90,200,216,0.5)", borderRadius: 6,
                  padding: "0.7rem 0.95rem", marginBottom: "1rem",
                  display: "flex", flexWrap: "wrap", gap: "0.7rem",
                  justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: "#F5EFE1", fontSize: "0.9rem", fontWeight: 700 }}>
          📡 Dispatch Approved · {approvedCount} contact{approvedCount === 1 ? "" : "s"} ready
        </div>
        <div style={{ color: "#ADBED1", fontSize: "0.75rem", marginTop: 3 }}>
          Routes each through the scrubber → Twilio SMS + Resend email + Stripe Connect Express onboarding link.
        </div>
      </div>
      <button
        type="button"
        data-testid="vh-dispatch-all-btn"
        onClick={dispatchAll}
        disabled={busy}
        style={{ background: "linear-gradient(90deg, #5AC8D8, #00DC78)", color: "#062617",
                 border: "none", padding: "0.55rem 1.2rem", borderRadius: 5,
                 fontSize: "0.85rem", fontWeight: 800,
                 cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Dispatching…" : `🚀 Dispatch All (${approvedCount})`}
      </button>
      {result && (
        <div data-testid="vh-dispatch-all-result" style={{ flexBasis: "100%", color: "#F5EFE1", fontSize: "0.8rem" }}>
          ✓ Approved: <strong>{result.approved_count}</strong> · Dispatched: <strong>{result.dispatched_count}</strong>
          {result.note && <span style={{ color: "#ADBED1" }}> · {result.note}</span>}
        </div>
      )}
      {err && (
        <div data-testid="vh-dispatch-all-err" style={{ flexBasis: "100%", color: "#FCA5A5", fontSize: "0.8rem" }}>
          ⚠ {err}
        </div>
      )}
    </div>
  );
};


const ManualTab = ({ onSaved }) => {
  const [form, setForm] = useState({
    business_name: "", trade: "cleaning", city: "", zip_code: "",
    phone: "", email: "", website: "",
    business_age_years: "3", employee_count: "1-5",
    owner_is_operator: true, is_franchise: false, has_public_parent: false,
    license_held_individually: true,
    black_owned: false, woman_owned: false, veteran_owned: false, lgbtq_owned: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.business_name.trim()) { alert("Business name is required."); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendor-hunter/prospect`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) onSaved();
      else {
        const d = await r.json().catch(() => ({}));
        alert(`Could not save: ${d.detail || r.status}`);
      }
    } catch { alert("Network error saving prospect."); }
    finally { setSaving(false); }
  };

  const input = (k, placeholder, type = "text") => (
    <input
      data-testid={`vh-manual-${k}`}
      type={type}
      value={form[k]}
      onChange={(e) => set(k, e.target.value)}
      placeholder={placeholder}
      style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
               border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
               padding: "0.5rem 0.7rem", fontSize: "0.85rem" }}
    />
  );

  const checkbox = (k, label) => (
    <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#F5EFE1", fontSize: "0.82rem", cursor: "pointer" }}>
      <input type="checkbox"
             data-testid={`vh-manual-${k}`}
             checked={form[k]}
             onChange={(e) => set(k, e.target.checked)} />
      {label}
    </label>
  );

  const label = (t) => <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 3 }}>{t}</div>;

  return (
    <div data-testid="vh-manual-tab" style={{ display: "grid", gap: "0.75rem", maxHeight: "62vh", overflowY: "auto", paddingRight: 4 }}>
      <div>{label("Business Name *")}{input("business_name", "Sample DFW Cleaning Co")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
        <div>{label("Trade")}
          <select data-testid="vh-manual-trade" value={form.trade}
                  onChange={(e) => set("trade", e.target.value)}
                  style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                           border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                           padding: "0.5rem 0.7rem", fontSize: "0.85rem" }}>
            <optgroup label="Tier A (direct crew)">
              <option value="cleaning">Cleaning</option>
              <option value="turnover_cleaning">Turnover Cleaning</option>
              <option value="punch_out">Punch-Out</option>
              <option value="cosmetic">Cosmetic Repair</option>
              <option value="cabinet_reface">Cabinet Reface</option>
              <option value="paint_touch_up">Paint / Touch-Up</option>
              <option value="carpet_install">Carpet Install</option>
              <option value="lock_change">Lock Change</option>
              <option value="modular_install">Modular Install</option>
            </optgroup>
            <optgroup label="Tier B (referral-only)">
              <option value="hvac">HVAC</option>
              <option value="electrical">Electrical</option>
              <option value="plumbing">Plumbing</option>
              <option value="pool">Pool</option>
              <option value="lead_paint">Lead Paint (EPA-RRP)</option>
              <option value="asbestos">Asbestos</option>
              <option value="mold_remediation">Mold Treatment</option>
              <option value="structural_roofing">Structural / Roofing</option>
              <option value="gutter">Gutter</option>
            </optgroup>
          </select>
        </div>
        <div>{label("Business Age (years)")}
          <select data-testid="vh-manual-age" value={form.business_age_years}
                  onChange={(e) => set("business_age_years", e.target.value)}
                  style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                           border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                           padding: "0.5rem 0.7rem", fontSize: "0.85rem" }}>
            <option value="0-3">0–3 yr (+25)</option>
            <option value="3-7">3–7 yr (+15)</option>
            <option value="7+">7+ yr (+0)</option>
          </select>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.6rem" }}>
        <div>{label("City")}{input("city", "Arlington")}</div>
        <div>{label("ZIP")}{input("zip_code", "76001")}</div>
        <div>{label("Employees")}
          <select data-testid="vh-manual-emp" value={form.employee_count}
                  onChange={(e) => set("employee_count", e.target.value)}
                  style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                           border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                           padding: "0.5rem 0.7rem", fontSize: "0.85rem" }}>
            <option value="1-5">1–5 (+20)</option>
            <option value="6-15">6–15 (+10)</option>
            <option value="16+">16+ (+0)</option>
          </select>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
        <div>{label("Phone")}{input("phone", "817-555-0100")}</div>
        <div>{label("Email")}{input("email", "hello@example.com", "email")}</div>
      </div>
      <div>{label("Website")}{input("website", "https://example.com")}</div>

      <div>{label("Operator Structure")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem 1rem" }}>
          {checkbox("owner_is_operator", "Owner works on-site (+15)")}
          {checkbox("license_held_individually", "License held individually (+10)")}
          {checkbox("is_franchise", "Franchise (auto-decline)")}
          {checkbox("has_public_parent", "Public parent co. (auto-decline)")}
        </div>
      </div>

      <div>{label("Community-First Tags (voluntary)")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem 1rem" }}>
          {checkbox("black_owned", "Black-owned")}
          {checkbox("woman_owned", "Woman-owned")}
          {checkbox("veteran_owned", "Veteran-owned")}
          {checkbox("lgbtq_owned", "LGBTQ+-owned")}
        </div>
      </div>

      <div>{label("Notes")}
        <textarea data-testid="vh-manual-notes" value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={3}
                  placeholder="Anything Shannon should know before deciding…"
                  style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                           border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                           padding: "0.5rem 0.7rem", fontSize: "0.85rem", resize: "vertical" }} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" data-testid="vh-manual-submit"
                onClick={submit} disabled={saving}
                style={{ background: "linear-gradient(90deg, #00DC78, #00B364)", color: "#062617",
                         border: "none", padding: "0.6rem 1.4rem", borderRadius: 5,
                         fontSize: "0.88rem", fontWeight: 800,
                         cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Scoring…" : "🎯 Score & Add to Queue"}
        </button>
      </div>
    </div>
  );
};


const CsvTab = ({ onImported }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    window.open(`${BACKEND_URL}/api/admin/vendor-hunter/template-csv`, "_blank");
  };

  const upload = async () => {
    if (!file) { alert("Pick a CSV first."); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${BACKEND_URL}/api/admin/vendor-hunter/upload-csv`, {
        method: "POST", credentials: "include", body: fd,
      });
      const d = await r.json();
      if (r.ok) {
        setResult(d);
        setTimeout(onImported, 1200);
      } else {
        alert(`Import failed: ${d.detail || r.status}`);
      }
    } catch { alert("Network error on upload."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="vh-csv-tab" style={{ display: "grid", gap: "0.9rem" }}>
      <div style={{ background: "rgba(184,145,90,0.08)", border: "1px solid rgba(184,145,90,0.3)",
                    borderRadius: 5, padding: "0.9rem 1rem" }}>
        <div style={{ color: "#F5EFE1", fontSize: "0.9rem", fontWeight: 700, marginBottom: 4 }}>
          📥 Step 1 — Download the template
        </div>
        <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginBottom: "0.6rem" }}>
          Fill in one vendor per row. Fields marked as booleans accept <code style={{ background: "rgba(0,0,0,0.3)", padding: "0 4px" }}>true</code> / <code style={{ background: "rgba(0,0,0,0.3)", padding: "0 4px" }}>false</code>.
        </div>
        <button type="button" data-testid="vh-csv-template"
                onClick={downloadTemplate}
                style={btnStyle("#B8915A", "#1b2a4e")}>
          ⬇ Download CSV Template
        </button>
      </div>

      <div style={{ background: "rgba(0,0,0,0.22)", border: "1px solid rgba(184,145,90,0.3)",
                    borderRadius: 5, padding: "0.9rem 1rem" }}>
        <div style={{ color: "#F5EFE1", fontSize: "0.9rem", fontWeight: 700, marginBottom: 4 }}>
          📤 Step 2 — Upload your filled CSV
        </div>
        <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginBottom: "0.6rem" }}>
          Each row is auto-scored against your Tier A / Tier B doctrine and dropped into the queue.
        </div>
        <input
          type="file"
          accept=".csv,text/csv"
          data-testid="vh-csv-file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ color: "#F5EFE1", fontSize: "0.82rem" }}
        />
        <div style={{ marginTop: "0.7rem" }}>
          <button type="button" data-testid="vh-csv-upload"
                  onClick={upload} disabled={busy || !file}
                  style={{ ...btnStyle("linear-gradient(90deg, #00DC78, #00B364)", "#062617"),
                           opacity: (busy || !file) ? 0.6 : 1,
                           cursor: (busy || !file) ? "not-allowed" : "pointer" }}>
            {busy ? "Importing…" : "🎯 Import & Auto-Score"}
          </button>
        </div>
      </div>

      {result && (
        <div data-testid="vh-csv-result" style={{ background: "rgba(0,220,120,0.1)", border: "1px solid rgba(0,220,120,0.4)",
                                                  borderRadius: 5, padding: "0.9rem 1rem", color: "#F5EFE1" }}>
          <div style={{ fontSize: "0.88rem", fontWeight: 700, marginBottom: 4 }}>
            ✓ Imported {result.inserted} prospects
          </div>
          <div style={{ color: "#ADBED1", fontSize: "0.8rem" }}>
            🟢 Auto-approve-eligible: <strong>{result.auto_approve_count}</strong> ·
            🟡 Needs review: <strong>{result.needs_review_count}</strong> ·
            🔴 Auto-decline: <strong>{result.auto_decline_count}</strong>
          </div>
        </div>
      )}
    </div>
  );
};


export default VendorHunterModal;
